// Beyond AI — Assistant (multi-turn streaming chat). Phase 7.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Plus, Send, Trash2, Loader2, MessageSquare, Bot, Building2, X, ChevronDown, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import Markdown from "@/components/Markdown";

const SUGGESTIONS = [
    "What counts as a higher-risk building under the BSA 2022?",
    "Summarise the fire door inspection frequencies for an 11m+ block.",
    "When must I report an injury under RIDDOR?",
    "What documents does CDM 2015 require before construction starts?",
];

export default function AssistantPage() {
    const [sessions, setSessions] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [denied, setDenied] = useState(false);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [focus, setFocus] = useState(null); // {type, id, label}
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickClient, setPickClient] = useState("");
    const scrollRef = useRef(null);
    const pickerRef = useRef(null);
    const [params, setParams] = useSearchParams();

    const loadSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/ai/sessions");
            setSessions(data);
            setDenied(false);
        } catch (e) {
            if (e.response?.status === 403) setDenied(true);
        }
    }, []);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
    }, []);

    // Deep-link focus: /app/ai-assist?context_type=building&context_id=..&context_label=..
    useEffect(() => {
        const t = params.get("context_type");
        const id = params.get("context_id");
        if (t && id) {
            setFocus({ type: t, id, label: params.get("context_label") || "Selected record" });
            setActiveId(null);
            setMessages([]);
            params.delete("context_type"); params.delete("context_id"); params.delete("context_label");
            setParams(params, { replace: true });
        }
    }, [params, setParams]);

    useEffect(() => {
        function onDoc(e) { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); }
        if (pickerOpen) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [pickerOpen]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming]);

    async function openSession(id) {
        if (streaming) return;
        try {
            const { data } = await api.get(`/ai/sessions/${id}`);
            setActiveId(id);
            setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
            const s = data.session;
            setFocus(s.context_type && s.context_id ? { type: s.context_type, id: s.context_id, label: s.context_label } : null);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    function newChat() {
        if (streaming) return;
        setActiveId(null);
        setMessages([]);
        setInput("");
    }

    function pickFocus(type, id, label) {
        setFocus({ type, id, label });
        setPickerOpen(false);
        setActiveId(null);
        setMessages([]);
    }

    async function removeSession(id, e) {
        e.stopPropagation();
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/ai/sessions/${id}`);
            if (id === activeId) newChat();
            loadSessions();
            toast.success("Conversation deleted");
        } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    }

    async function send(text) {
        const message = (text ?? input).trim();
        if (!message || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const body = { session_id: activeId, message };
            if (!activeId && focus) { body.context_type = focus.type; body.context_id = focus.id; }
            const resp = await fetch(`${API}/ai/chat`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!resp.ok || !resp.body) {
                throw new Error(resp.status === 403 ? "You don't have access to the AI assistant." : `Request failed (${resp.status})`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let newSessionId = activeId;
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buf.indexOf("\n\n")) >= 0) {
                    const chunk = buf.slice(0, idx).trim();
                    buf = buf.slice(idx + 2);
                    if (!chunk.startsWith("data:")) continue;
                    let data;
                    try { data = JSON.parse(chunk.slice(5).trim()); } catch { continue; }
                    if (data.session_id) {
                        newSessionId = data.session_id;
                        if (!activeId) setActiveId(data.session_id);
                    } else if (data.delta) {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                            return copy;
                        });
                    } else if (data.error) {
                        toast.error(data.error);
                    }
                }
            }
            setActiveId(newSessionId);
            loadSessions();
        } catch (e) {
            toast.error(e.message || "Assistant error");
            setMessages((m) => m.slice(0, -1)); // drop empty assistant bubble
        } finally {
            setStreaming(false);
        }
    }

    if (denied) {
        return (
            <div data-testid="ai-assistant-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to the AI assistant.</p>
            </div>
        );
    }

    return (
        <div data-testid="ai-assistant-page" className="flex gap-4 h-[calc(100vh-150px)]">
            {/* Sessions rail */}
            <div className="w-[240px] shrink-0 bg-white border rounded-[14px] flex flex-col" style={{ borderColor: "#E1E8F2" }}>
                <div className="p-3 border-b" style={{ borderColor: "#EEF3FB" }}>
                    <button data-testid="ai-new-chat-button" onClick={newChat}
                        className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white bg-ai-grad">
                        <Plus className="w-3.5 h-3.5" /> New chat
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="ai-session-list">
                    {sessions.length === 0 && <div className="text-[11.5px] italic text-center py-6" style={{ color: "#9A968F" }}>No conversations yet.</div>}
                    {sessions.map((s) => (
                        <div key={s.id} data-testid={`ai-session-${s.id}`} onClick={() => openSession(s.id)}
                            className={`group flex items-center gap-2 px-2.5 py-2 rounded-[8px] cursor-pointer text-[12px] ${activeId === s.id ? "bg-[#EEF3FB] font-semibold" : "hover:bg-[#F6F8FB]"}`}>
                            <MessageSquare className="w-3.5 h-3.5 shrink-0" style={{ color: "#1F6FE5" }} />
                            <span className="flex-1 truncate text-[#1F1F1F]">{s.title}</span>
                            <button data-testid={`ai-session-delete-${s.id}`} onClick={(e) => removeSession(s.id, e)}
                                className="opacity-0 group-hover:opacity-100 text-[#B33A2E]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chat pane */}
            <div className="flex-1 bg-white border rounded-[14px] flex flex-col min-w-0" style={{ borderColor: "#E1E8F2" }}>
                <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "#EEF3FB" }}>
                    <Sparkles className="w-4 h-4" style={{ color: "#1F6FE5" }} />
                    <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-bold text-[#1F1F1F]">Beyond AI assistant</div>
                        <div className="text-[10.5px]" style={{ color: "#6F6A64" }}>Grounded in UK H&amp;S, Building Safety Act, CDM &amp; fire safety</div>
                    </div>
                    {/* Focus control */}
                    <div className="relative" ref={pickerRef}>
                        {focus ? (
                            <div data-testid="ai-focus-chip" className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-full bg-[#EEF3FB] text-[#1F6FE5] text-[11.5px] font-semibold max-w-[260px]">
                                <Crosshair className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">{focus.label}</span>
                                <button data-testid="ai-focus-clear" onClick={() => { setFocus(null); if (!activeId) return; newChat(); }} className="w-5 h-5 grid place-items-center rounded-full hover:bg-white/70"><X className="w-3 h-3" /></button>
                            </div>
                        ) : (
                            <button data-testid="ai-focus-trigger" onClick={() => { setPickerOpen((o) => !o); setPickClient(""); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11.5px] font-semibold text-[#1F6FE5]" style={{ borderColor: "#BFD8F5" }}>
                                <Crosshair className="w-3.5 h-3.5" /> Focus on a record <ChevronDown className="w-3 h-3" />
                            </button>
                        )}
                        {pickerOpen && !focus && (
                            <div data-testid="ai-focus-picker" className="absolute right-0 mt-2 w-[280px] bg-white border rounded-[11px] shadow-brand-lg p-2 z-40 max-h-[60vh] overflow-y-auto" style={{ borderColor: "#E1E8F2" }}>
                                <div className="px-2 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>Step 1 · Client</div>
                                <select data-testid="ai-focus-client" value={pickClient} onChange={(e) => setPickClient(e.target.value)} className="w-full mb-1 px-2.5 py-2 rounded-[8px] border text-[12px]" style={{ borderColor: "#E1E8F2" }}>
                                    <option value="">Select a client…</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                {pickClient && (
                                    <>
                                        <button data-testid="ai-focus-whole-client" onClick={() => pickFocus("client", pickClient, clients.find((c) => c.id === pickClient)?.name)}
                                            className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-[8px] hover:bg-[#F6F8FB] text-[12px] font-semibold text-[#1F1F1F]">
                                            <Building2 className="w-3.5 h-3.5" style={{ color: "#1F6FE5" }} /> Whole client overview
                                        </button>
                                        <div className="px-2 pt-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>Or a building</div>
                                        {buildings.filter((b) => b.client_id === pickClient).map((b) => (
                                            <button key={b.id} data-testid={`ai-focus-building-${b.id}`} onClick={() => pickFocus("building", b.id, b.name)}
                                                className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-[8px] hover:bg-[#F6F8FB] text-[12px] text-[#2D2D2D]">
                                                <Building2 className="w-3.5 h-3.5 shrink-0" style={{ color: "#9A968F" }} /> <span className="truncate">{b.name}</span>
                                            </button>
                                        ))}
                                        {buildings.filter((b) => b.client_id === pickClient).length === 0 && <div className="px-2.5 py-2 text-[11px] italic" style={{ color: "#9A968F" }}>No buildings for this client.</div>}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ai-message-thread">
                    {messages.length === 0 && (
                        <div className="h-full flex flex-col items-center justify-center text-center px-6">
                            <div className="w-14 h-14 rounded-2xl bg-ai-grad-soft grid place-items-center mb-3" style={{ border: "1px solid #BFD8F5" }}>
                                <Bot className="w-7 h-7" style={{ color: "#1F6FE5" }} />
                            </div>
                            <div className="text-[15px] font-bold text-[#1F1F1F]">How can I help?</div>
                            {focus ? (
                                <p data-testid="ai-focus-note" className="text-[12px] mt-1 mb-4 max-w-md inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EEF3FB]" style={{ color: "#1F6FE5" }}>
                                    <Crosshair className="w-3.5 h-3.5" /> Focused on <b>{focus.label}</b> — answers use this record's live data.
                                </p>
                            ) : (
                                <p className="text-[12px] mt-1 mb-4 max-w-md" style={{ color: "#6F6A64" }}>Ask about regulations, draft guidance, or get help interpreting a finding. Tip: use <b>Focus on a record</b> to ground answers in a specific building.</p>
                            )}
                            <div className="grid sm:grid-cols-2 gap-2 max-w-xl w-full">
                                {SUGGESTIONS.map((s, idx) => (
                                    <button key={idx} data-testid={`ai-suggestion-${idx}`} onClick={() => send(s)}
                                        className="text-left text-[11.5px] px-3 py-2 rounded-[9px] border bg-[#FAFCFF] hover:bg-[#EEF3FB] transition-colors text-[#2D2D2D]" style={{ borderColor: "#E1E8F2" }}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {messages.map((m, idx) => (
                        <div key={idx} data-testid={`ai-message-${m.role}-${idx}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[78%] rounded-[14px] px-4 py-2.5 ${m.role === "user" ? "bg-[#1F1F1F] text-white" : "bg-[#F6F8FB] border"}`} style={m.role === "user" ? {} : { borderColor: "#E1E8F2" }}>
                                {m.role === "user"
                                    ? <div className="text-[12.7px] leading-relaxed whitespace-pre-wrap">{m.content}</div>
                                    : (m.content ? <Markdown text={m.content} /> : <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#1F6FE5" }} />)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="px-4 py-3 border-t" style={{ borderColor: "#EEF3FB" }}>
                    <div className="flex items-end gap-2">
                        <textarea data-testid="ai-chat-input" value={input} rows={1}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder="Ask Beyond AI…  (Enter to send, Shift+Enter for a new line)"
                            className="flex-1 resize-none max-h-32 px-3.5 py-2.5 rounded-[10px] border text-[12.7px] outline-none focus:border-[#1F6FE5] focus:shadow-[0_0_0_3px_#E3EEFD]" style={{ borderColor: "#E1E8F2" }} />
                        <button data-testid="ai-chat-send" onClick={() => send()} disabled={streaming || !input.trim()}
                            className="h-[42px] px-4 rounded-[10px] text-white bg-ai-grad inline-flex items-center gap-1.5 text-[12.5px] font-semibold disabled:opacity-50">
                            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
